import React, { createContext, useState } from "react";
import { useQuery } from "@apollo/client";
import { PracticeReview, PRPhaseCode, ReviewedClient } from "./models";
import { Grid, Tab, Tabs } from "@mui/material";
import { Skeleton } from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import { AppRoute } from "Routes";
import { EstimateTime } from "scheduling/estimate-time/EstimateTime";
import { SchedulingAndReviewers } from "scheduling/scheduling-reviewers/SchedulingAndReviewers";
import { TimeEntries } from "time-entry/TimeEntries";
import { FirmDetails } from "practice-reviews/FirmDetails";
import { ReportsAndQuestionnaires } from "practice-reviews/ReportsAndQuestionnaires";
import { FirmChecklist } from "checklists/FirmChecklist";
import { ReviewedClients } from "practice-reviews/reviewed-clients/ReviewedClients";
import { Helmet } from "react-helmet";
import ActivityLog from "activities/ActivityLog";
import ReviewHistory from "./ReviewHistory";
import { DecisionTab } from "decisions/DecisionTab";
import { SummaryTab } from "./summary/SummaryTab";
import { PrScreenQuery } from "practice-reviews";
import { makeStyles } from "makeStyles";
import { useCurrentUser, Permissions } from "users";
import {
  AvailableMeetingsQuery,
  CommitteeMeeting,
  getReasonPrMeetingCannotBeChanged,
} from "../committee-meetings";
import { DecisionTypeCode } from "../decisions";
import { ReviewedClientChecklists } from "../checklists/ReviewedClientChecklists";
import { FirmResponseTab } from "../firm-response/FirmResponseTab";
import { MonitoringTab } from "../monitoring/MonitoringTab";
import { PrScreenHeader, getPrScreenHeaderHeight } from "./PrScreenHeader";
import { ExemptionTab } from "../exemptions/ExemptionTab";
import { getMonitorDirectedPDInaccessibleReason } from "decisions/decisionLogic";
import { DirectedPdTab } from "./DirectedPdTab";
import { ProgramDetailsTab } from "../pprp/ProgramDetailsTab";
import { PrOverview } from "./PrOverview";
import {
  getReasonPrStatusCannotBeChanged,
  getReasonPrTypeCannotBeChanged,
} from "./utilities";

export const PracticeReviewContext = createContext<{
  practiceReview: PracticeReview | undefined;
}>({
  practiceReview: undefined,
});

export enum PracticeReviewTabs {
  Overview = "overview",
  FirmDetails = "firm-details",
  ProgramDetails = "program-details",
  Exemption = "exemption",
  History = "history",
  ActivityLog = "activity-log",
  EstimateTime = "estimate-time",
  SchedulingReviewers = "scheduling-reviewers",
  ReportsQuestionnaires = "reports-questionnaires",
  FirmChecklist = "firm-checklist",
  ClientFiles = "client-files",
  ClientChecklists = "client-checklists",
  Candidates = "candidates",
  CandidateChecklists = "candidate-checklists",
  Decision = "decision",
  DirectedPD = "directed-pd",
  Monitoring = "monitoring",
  TimeEntry = "time-entry",
  FirmResponse = "firm-response",
  Summary = "summary",
}

export function getRouteForPracticeReview(
  practiceReview: PracticeReview | null,
  tab?: PracticeReviewTabs,
  client?: ReviewedClient,
  decisionTypeCode?: DecisionTypeCode
) {
  return practiceReview
    ? getRouteForPracticeReviewNumber(
        practiceReview.prNumber,
        tab,
        client,
        decisionTypeCode
      )
    : "";
}

export function getRouteForPracticeReviewNumber(
  prNumber: string,
  tab?: PracticeReviewTabs,
  reviewedClient?: ReviewedClient,
  decisionTypeCode?: DecisionTypeCode
) {
  if (tab === PracticeReviewTabs.Decision) {
    return AppRoute.PracticeReviewDecision.replace(
      ":prNumber",
      prNumber
    ).replace("/:decisionTab?", decisionTypeCode ? `/${decisionTypeCode}` : "");
  } else if (tab === PracticeReviewTabs.ClientChecklists) {
    return AppRoute.PracticeReviewClientChecklists.replace(
      ":prNumber",
      prNumber
    ).replace(
      "/:reviewedClientId?",
      reviewedClient?.id ? `/${reviewedClient.id}` : ""
    );
  } else if (tab === PracticeReviewTabs.CandidateChecklists) {
    return AppRoute.PracticeReviewCandidateChecklists.replace(
      ":prNumber",
      prNumber
    ).replace(
      "/:reviewedClientId?",
      reviewedClient?.id ? `/${reviewedClient.id}` : ""
    );
  } else
    return AppRoute.PracticeReview.replace(":prNumber", prNumber).replace(
      "/:tab?",
      tab ? `/${tab}` : ""
    );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: theme.palette.common.white,
    "& .MuiTabPanel-root": {
      flexGrow: 1,
      height: "100%",
    },
  },
  content: {
    flexGrow: 1,
    display: "flex",
    alignItems: "stretch",
  },
  tabContent: {
    flexGrow: 1,
    borderLeft: `1px solid ${theme.palette.divider}`,
    minHeight: `calc(100vh - ${getPrScreenHeaderHeight(theme)})`,
  },
  tabs: {
    alignSelf: "flex-start",
    position: "sticky",
    top: getPrScreenHeaderHeight(theme),
    left: 0,
    width: "11em",
    minHeight: `calc(100vh - ${getPrScreenHeaderHeight(theme)})`,
    backgroundColor: theme.palette.primary.main,
    flexShrink: 0,
    "& .MuiTabs-flexContainer": {
      alignItems: "stretch",
    },
    "& .MuiTab-root": {
      color: theme.palette.common.white,
      minWidth: "unset",
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      alignItems: "flex-start",
      textAlign: "left",
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    "& .MuiTabs-indicator": {
      display: "none",
    },
  },
  unpaddedTabPanel: {
    padding: 0,
  },
}));

export const PracticeReviewScreen: React.FunctionComponent = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const currentUser = useCurrentUser();
  const { userHasPermission, userIsLeadReviewer } = currentUser;

  const routingParams = useParams<{
    prNumber: string;
    tab?: string;
    reviewedClientId?: string;
    decisionTab?: DecisionTypeCode;
  }>();
  const reviewedClientId = routingParams.reviewedClientId
    ? parseInt(routingParams.reviewedClientId)
    : undefined;

  const clientChecklistRouteMatch = useRouteMatch(
    AppRoute.PracticeReviewClientChecklists
  );
  const candidateChecklistRouteMatch = useRouteMatch(
    AppRoute.PracticeReviewCandidateChecklists
  );
  const decisionRouteMatch = useRouteMatch(AppRoute.PracticeReviewDecision);
  let routeTab = clientChecklistRouteMatch
    ? PracticeReviewTabs.ClientChecklists
    : candidateChecklistRouteMatch
      ? PracticeReviewTabs.CandidateChecklists
      : decisionRouteMatch
        ? PracticeReviewTabs.Decision
        : routingParams.tab;

  const [activeTab, setActiveTab] = useState(
    routeTab ?? PracticeReviewTabs.Overview
  );

  // Check route tab permissions
  if (
    routeTab === PracticeReviewTabs.SchedulingReviewers &&
    !userHasPermission(Permissions.ViewSchedulingAndReviewers)
  ) {
    routeTab = undefined;
  }

  if (routeTab !== undefined && activeTab !== routeTab) {
    // otherwise you can't switch from tab to tab by just pushing history, because the active tab already has a value...
    setActiveTab(() => routeTab as string);
  }

  const practiceReviewQuery = useQuery<{
    practiceReviewFromPrNumber: PracticeReview;
  }>(PrScreenQuery, {
    variables: {
      prNumber: routingParams.prNumber,
    },
  });
  const practiceReview = practiceReviewQuery.data?.practiceReviewFromPrNumber;

  const userHasPermissionToViewWholePr = practiceReview?.isPprpProgramReview
    ? userHasPermission(Permissions.PprpProgramReviewViewAll)
    : userHasPermission(Permissions.PracticeReviewViewAll);
  const userIsMerelyNonLeadReviewer =
    !userIsLeadReviewer(practiceReview) && !userHasPermissionToViewWholePr;

  const directedPDTabInaccessibleReason =
    getMonitorDirectedPDInaccessibleReason(practiceReview);

  const reviewIsNotReadyToPerform =
    (practiceReview?.phase.id ?? 0) < PRPhaseCode.PerformPracticeReview;

  const availableMeetingsQuery = useQuery<{
    availableMeetings: CommitteeMeeting[];
  }>(AvailableMeetingsQuery);
  const availableMeetings =
    availableMeetingsQuery.data?.availableMeetings ?? [];
  const reasonPrMeetingCannotBeChanged = getReasonPrMeetingCannotBeChanged(
    practiceReview,
    availableMeetings
  );
  const reasonPrTypeCannotBeChanged =
    getReasonPrTypeCannotBeChanged(practiceReview);
  const reasonPrStatusCannotBeChanged = getReasonPrStatusCannotBeChanged(
    practiceReview,
    currentUser
  );
  return (
    <PracticeReviewContext.Provider
      value={{
        practiceReview: practiceReview,
      }}
      key={practiceReview?.id}
    >
      <div className={classes.root}>
        <Helmet>
          <title>
            {practiceReview
              ? `PR ${practiceReview.prNumber} - PRS Online`
              : `Practice Review - PRS Online`}
          </title>
        </Helmet>

        {(practiceReviewQuery.loading && !practiceReviewQuery.data) ||
        !practiceReview ? (
          <Grid container direction="column" spacing={3} sx={{ p: 3 }}>
            <Grid item xs={12}>
              <Skeleton variant="rectangular" width="100%" height="8rem" />
            </Grid>
            <Grid item xs={12}>
              <Skeleton variant="rectangular" width="100%" height="50rem" />
            </Grid>
          </Grid>
        ) : (
          <>
            <PrScreenHeader
              practiceReview={practiceReview}
              reasonPrMeetingCannotBeChanged={reasonPrMeetingCannotBeChanged}
              reasonPrTypeCannotBeChanged={reasonPrTypeCannotBeChanged}
              reasonPrStatusCannotBeChanged={reasonPrStatusCannotBeChanged}
            />
            <div className={classes.content}>
              <Tabs
                value={activeTab}
                indicatorColor="primary"
                textColor="primary"
                orientation="vertical"
                className={classes.tabs}
                onChange={(e, newTab) => {
                  setActiveTab(newTab);
                  history.push(
                    getRouteForPracticeReview(practiceReview, newTab)
                  );
                }}
              >
                <Tab label="Overview" value={PracticeReviewTabs.Overview} />
                {!practiceReview.isPprpProgramReview && (
                  <Tab
                    label="Firm Details"
                    value={PracticeReviewTabs.FirmDetails}
                  />
                )}
                {practiceReview.isPprpProgramReview &&
                  practiceReview.pprpProgram && (
                    <Tab
                      label="Program Details"
                      value={PracticeReviewTabs.ProgramDetails}
                    />
                  )}
                {practiceReview.exemption && (
                  <Tab label="Exemption" value={PracticeReviewTabs.Exemption} />
                )}
                <Tab label="History" value={PracticeReviewTabs.History} />
                <Tab
                  label="Activity Log"
                  value={PracticeReviewTabs.ActivityLog}
                />
                {!practiceReview.isPprpProgramReview && (
                  <Tab
                    label="Estimate Time"
                    value={PracticeReviewTabs.EstimateTime}
                  />
                )}
                {userHasPermission(Permissions.ViewSchedulingAndReviewers) && (
                  <Tab
                    label="Scheduling / Reviewers"
                    value={PracticeReviewTabs.SchedulingReviewers}
                  />
                )}
                <Tab
                  label="Reports / Questionnaires"
                  value={PracticeReviewTabs.ReportsQuestionnaires}
                />
                <Tab
                  label="Summary"
                  value={PracticeReviewTabs.Summary}
                  disabled={reviewIsNotReadyToPerform}
                />
                {!userIsMerelyNonLeadReviewer && (
                  <Tab
                    label="Firm Checklist"
                    value={PracticeReviewTabs.FirmChecklist}
                    disabled={reviewIsNotReadyToPerform}
                  />
                )}
                {!practiceReview.isPprpProgramReview && (
                  <Tab
                    label="Client Files"
                    value={PracticeReviewTabs.ClientFiles}
                    disabled={reviewIsNotReadyToPerform}
                  />
                )}
                {!practiceReview.isPprpProgramReview && (
                  <Tab
                    label="Client Checklists"
                    value={PracticeReviewTabs.ClientChecklists}
                    disabled={reviewIsNotReadyToPerform}
                  />
                )}
                {practiceReview.isPprpProgramReview && (
                  <Tab
                    label="Candidates"
                    value={PracticeReviewTabs.Candidates}
                    disabled={reviewIsNotReadyToPerform}
                  />
                )}
                {practiceReview.isPprpProgramReview && (
                  <Tab
                    label="Candidate Checklists"
                    value={PracticeReviewTabs.CandidateChecklists}
                    disabled={reviewIsNotReadyToPerform}
                  />
                )}
                {!userIsMerelyNonLeadReviewer && (
                  <Tab
                    label="Decision"
                    value={PracticeReviewTabs.Decision}
                    disabled={reviewIsNotReadyToPerform}
                  />
                )}
                <Tab
                  label="Time Entry"
                  value={PracticeReviewTabs.TimeEntry}
                  disabled={reviewIsNotReadyToPerform}
                />
                {!userIsMerelyNonLeadReviewer && (
                  <Tab
                    label="Firm Response"
                    value={PracticeReviewTabs.FirmResponse}
                    disabled={reviewIsNotReadyToPerform}
                  />
                )}
                {!Boolean(directedPDTabInaccessibleReason) && (
                  <Tab
                    value={PracticeReviewTabs.DirectedPD}
                    label={<span>Directed PD</span>}
                  />
                )}
                {practiceReview.completedDate &&
                  practiceReview.isPprpProgramReview &&
                  practiceReview.monitorings?.length > 0 && (
                    <Tab
                      label="Monitoring"
                      value={PracticeReviewTabs.Monitoring}
                    />
                  )}
              </Tabs>
              <div className={classes.tabContent}>
                <TabContext value={activeTab}>
                  <TabPanel value={PracticeReviewTabs.Overview}>
                    <PrOverview
                      practiceReview={practiceReview}
                      reasonPrMeetingCannotBeChanged={
                        reasonPrMeetingCannotBeChanged
                      }
                    />
                  </TabPanel>
                  <TabPanel value={PracticeReviewTabs.FirmDetails}>
                    <FirmDetails practiceReview={practiceReview} />
                  </TabPanel>
                  {practiceReview.exemption && (
                    <TabPanel value={PracticeReviewTabs.Exemption}>
                      <ExemptionTab practiceReview={practiceReview} />
                    </TabPanel>
                  )}
                  {practiceReview.pprpProgram && (
                    <TabPanel value={PracticeReviewTabs.ProgramDetails}>
                      <ProgramDetailsTab practiceReview={practiceReview} />
                    </TabPanel>
                  )}
                  <TabPanel value={PracticeReviewTabs.History}>
                    <ReviewHistory practiceReview={practiceReview} />
                  </TabPanel>
                  <TabPanel value={PracticeReviewTabs.ActivityLog}>
                    <ActivityLog practiceReview={practiceReview} />
                  </TabPanel>
                  <TabPanel value={PracticeReviewTabs.EstimateTime}>
                    <EstimateTime practiceReview={practiceReview} />
                  </TabPanel>
                  <TabPanel value={PracticeReviewTabs.SchedulingReviewers}>
                    <SchedulingAndReviewers practiceReview={practiceReview} />
                  </TabPanel>
                  <TabPanel value={PracticeReviewTabs.Summary}>
                    <SummaryTab practiceReview={practiceReview} />
                  </TabPanel>
                  <TabPanel value={PracticeReviewTabs.ReportsQuestionnaires}>
                    <ReportsAndQuestionnaires practiceReview={practiceReview} />
                  </TabPanel>
                  <TabPanel
                    value={PracticeReviewTabs.FirmChecklist}
                    className={classes.unpaddedTabPanel}
                  >
                    <FirmChecklist
                      practiceReviewId={practiceReview.id}
                      prNumber={practiceReview.prNumber}
                      levelOfAdjustment={practiceReview.levelOfAdjustment}
                    />
                  </TabPanel>
                  <TabPanel
                    value={PracticeReviewTabs.ClientFiles}
                    className={classes.unpaddedTabPanel}
                  >
                    <ReviewedClients practiceReview={practiceReview} />
                  </TabPanel>
                  <TabPanel
                    value={PracticeReviewTabs.ClientChecklists}
                    className={classes.unpaddedTabPanel}
                  >
                    <ReviewedClientChecklists
                      practiceReview={practiceReview}
                      reviewedClientId={reviewedClientId}
                    />
                  </TabPanel>
                  <TabPanel
                    value={PracticeReviewTabs.Candidates}
                    className={classes.unpaddedTabPanel}
                  >
                    <ReviewedClients practiceReview={practiceReview} />
                  </TabPanel>
                  <TabPanel
                    value={PracticeReviewTabs.CandidateChecklists}
                    className={classes.unpaddedTabPanel}
                  >
                    <ReviewedClientChecklists
                      practiceReview={practiceReview}
                      reviewedClientId={reviewedClientId}
                    />
                  </TabPanel>
                  <TabPanel
                    value={PracticeReviewTabs.Decision}
                    className={classes.unpaddedTabPanel}
                  >
                    <DecisionTab
                      practiceReview={practiceReview}
                      subtab={routingParams.decisionTab}
                      setActiveDecisionTab={(newTab) =>
                        history.push(
                          getRouteForPracticeReview(
                            practiceReview,
                            PracticeReviewTabs.Decision,
                            undefined,
                            newTab
                          )
                        )
                      }
                    />
                  </TabPanel>
                  <TabPanel value={PracticeReviewTabs.TimeEntry}>
                    <TimeEntries practiceReview={practiceReview} />
                  </TabPanel>
                  <TabPanel value={PracticeReviewTabs.FirmResponse}>
                    <FirmResponseTab practiceReview={practiceReview} />
                  </TabPanel>
                  {!Boolean(directedPDTabInaccessibleReason) && (
                    <TabPanel value={PracticeReviewTabs.DirectedPD}>
                      <DirectedPdTab practiceReview={practiceReview} />
                    </TabPanel>
                  )}
                  {practiceReview.monitorings?.length > 0 && (
                    <TabPanel value={PracticeReviewTabs.Monitoring}>
                      <MonitoringTab
                        reviewOrItem={practiceReview}
                        containerType="review"
                      />
                    </TabPanel>
                  )}
                </TabContext>
              </div>
            </div>
          </>
        )}
      </div>
    </PracticeReviewContext.Provider>
  );
};
